"use client";

import { initOrderAction, validateAddressAction } from "@/app/actions";
import { EfnAuth } from "@/services/auth/types";
import Link from "next/link";
import { redirect } from "next/navigation";
import { useState } from "react";
import { useFormState } from "react-dom";
import Button from "../button";
import Divider from "../divider";
import InputCheckbox from "../form/input-checkbox";
import InputField from "../form/input-field";
import InputGroup from "../form/input-group";
import InputPhone from "../form/input-phone";
import { Package } from "./data";

type DetailsFormProps = {
  product: Package;
  user: EfnAuth.User | null;
  isApp: boolean;
  promoCode?: string;
  companyRequired?: boolean;
  prefix: string;
};

const DetailsForm = (props: DetailsFormProps) => {
  const [showShippingInformation, setShowShippingInformation] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [street, setStreet] = useState(props.user?.address || "");
  const [zip, setZip] = useState(props.user?.zip || "");
  const [city, setCity] = useState(props.user?.city || "");
  const [shippingStreet, setShippingStreet] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingCity, setShippingCity] = useState("");

  const [_, action] = useFormState(async (_: any, formData: FormData) => {
    setAddressError(null);
    setShippingError(null);
    setSubmitError(null);

    const billingValidation = await validateAddressAction({
      street: formData.get("user.address")?.toString() || "",
      streetNumber: "",
      postalCode: formData.get("user.postalCode")?.toString() || "",
      city: formData.get("user.city")?.toString() || "",
    });

    const shippingValidation = showShippingInformation
      ? await validateAddressAction({
          street: formData.get("shippingAddress.address")?.toString() || "",
          streetNumber: "",
          postalCode:
            formData.get("shippingAddress.postalCode")?.toString() || "",
          city: formData.get("shippingAddress.city")?.toString() || "",
        })
      : null;

    if (!billingValidation.valid) {
      setAddressError(billingValidation.message);
    }
    if (shippingValidation && !shippingValidation.valid) {
      setShippingError(shippingValidation.message);
    }
    if (
      !billingValidation.valid ||
      (shippingValidation && !shippingValidation.valid)
    ) {
      return null;
    }

    const countryCode = formData.get("countryCode")?.toString() || "";
    const phone = formData.get("phone")?.toString() || "";

    const completePhone = phone
      ? `${countryCode}${phone.startsWith("0") ? phone.slice(1) : phone}`
      : undefined;

    const combinedAddress =
      formData.get("user.address")?.toString().trim() || "";

    const combinedShippingAddress =
      formData.get("shippingAddress.address")?.toString().trim() || "";

    const data = {
      user: {
        firstname: formData.get("user.firstname")?.toString() || "",
        lastname: formData.get("user.lastname")?.toString() || "",
        phone: completePhone,
        email: formData.get("user.email")?.toString() || "",
        address: combinedAddress || undefined,
        postalCode: formData.get("user.postalCode")?.toString(),
        city: formData.get("user.city")?.toString(),
        company: formData.get("user.company")?.toString(),
        tocAccept: Boolean(formData.get("user.tocAccept")),
      },
      packageId: Number(formData.get("packageId")),
      promoCode: formData.get("promoCode")?.toString(),
      shippingAddress: showShippingInformation
        ? {
            co: formData.get("shippingAddress.co")?.toString(),
            address: combinedShippingAddress,
            city: formData.get("shippingAddress.city")?.toString() || "",
            postalCode:
              formData.get("shippingAddress.postalCode")?.toString() || "",
          }
        : undefined,
      isapp: props.isApp,
      redirectUrl: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${props.prefix}/order?orderId={order.id}`,
    };

    let response;
    try {
      response = await initOrderAction(data);
    } catch (e) {
      setSubmitError("Något gick fel. Försök igen.");
      return null;
    }

    if (response.subscriptionIsActive) {
      if (props.user) {
        redirect(`/${props.prefix}/bekraftelse?orderid=${response.order.id}`);
      } else {
        redirect(`/${props.prefix}/info`);
      }
    }

    if (props.user) {
      redirect(`/${props.prefix}/order?orderId=${response.order.id}`);
    } else {
      redirect(`/${props.prefix}/info`);
    }
  }, null);

  return (
    <form className="space-y-12" action={action}>
      <input name="packageId" defaultValue={props.product.id} hidden />

      <InputGroup
        header="Värdekod"
        className="grid grid-cols-1 sm:grid-cols-2 gap-8"
      >
        <InputField
          label="Värdekod"
          type="text"
          placeholder="Ange värdekod här"
          name="promoCode"
          defaultValue={props.promoCode ?? ""}
        />

        <p className="font-semibold text-sm">
          Priset justeras automatiskt så länge ditt <br />
          personliga erbjudande gäller.
        </p>
      </InputGroup>

      <Divider />

      <InputGroup
        header="Kontaktuppgifter"
        className="grid grid-cols-1 sm:grid-cols-2 gap-8"
      >
        <InputField
          label="Förnamn"
          type="text"
          placeholder="Ange förnamn"
          name="user.firstname"
          defaultValue={props.user?.firstname}
          readOnly={Boolean(props.user?.firstname)}
          required
          autoComplete="given-name"
        />

        <InputField
          label="Efternamn"
          type="text"
          placeholder="Ange efternamn"
          name="user.lastname"
          defaultValue={props.user?.lastname}
          readOnly={Boolean(props.user?.lastname)}
          required
          autoComplete="family-name"
        />

        <InputPhone defaultValue={props.user?.phone} />

        <InputField
          label="E-postadress"
          name="user.email"
          type="email"
          placeholder="Ange e-post"
          defaultValue={props.user?.email}
          readOnly={Boolean(props.user?.email)}
          required
          autoComplete="email"
        />

        <InputField
          label="Företagsnamn"
          name="user.company"
          type="text"
          placeholder="Ange företagsnamn"
          defaultValue={props.user?.company}
          readOnly={Boolean(props.user?.company)}
          pattern="^(?!.*(\d.*){9}).*$"
          helpText={props.companyRequired ? undefined : ""}
          autoComplete="organization"
          required={props.companyRequired}
        />
      </InputGroup>

      <Divider />

      <>
        <InputGroup
          header="Adress"
          className="grid grid-cols-1 sm:grid-cols-2 gap-8"
        >
          <InputField
            label="Gatuadress"
            name="user.address"
            type="text"
            placeholder="Ange gatuadress och nummer"
            required
            value={street}
            readOnly={Boolean(props.user?.address)}
            onChange={(e) => setStreet((e.target as HTMLInputElement).value)}
            autoComplete="address-line1"
            containerClassName="col-span-2 sm:col-auto"
            error={addressError ? " " : undefined}
          />

          <div className="grid grid-cols-2 gap-4 col-span-2 sm:col-auto">
            <InputField
              label="Postnummer"
              name="user.postalCode"
              required
              type="text"
              inputMode="numeric"
              placeholder="Postnummer"
              value={zip}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value.replace(
                  /\D/g,
                  "",
                );
                setZip(value);
              }}
              autoComplete="postal-code"
              error={addressError ? " " : undefined}
            />
            <InputField
              label="Ort"
              name="user.city"
              required
              type="text"
              value={city}
              onChange={(e) => setCity((e.target as HTMLInputElement).value)}
              placeholder="Ange ort"
              autoComplete="address-level2"
              error={addressError ? " " : undefined}
            />
          </div>
          {addressError && (
            <p className="col-span-2 text-sm text-red-500">{addressError}</p>
          )}
        </InputGroup>

        <button
          className="btn"
          type="button"
          onClick={() => setShowShippingInformation(!showShippingInformation)}
        >
          {showShippingInformation
            ? "Samma leveransadress"
            : "Annan leveransadress"}
        </button>

        {showShippingInformation && (
          <InputGroup
            header="Leveransadress"
            className="grid grid-cols-1 sm:grid-cols-2 gap-8 "
          >
            <InputField
              label="C/O adress"
              name="shippingAddress.co"
              type="text"
              placeholder="Ange C/O adress"
              containerClassName="col-span-2 sm:col-auto"
              autoComplete="address-line1"
            />

            <InputField
              label="Gatuadress"
              name="shippingAddress.address"
              type="text"
              placeholder="Ange gatuadress och nummer"
              required
              value={shippingStreet}
              onChange={(e) =>
                setShippingStreet((e.target as HTMLInputElement).value)
              }
              autoComplete="address-line1"
              containerClassName="col-span-2 sm:col-auto"
              error={shippingError ? " " : undefined}
            />

            <div className="grid grid-cols-2 gap-4 col-span-2 sm:col-auto">
              <InputField
                label="Postnummer"
                name="shippingAddress.postalCode"
                required
                type="text"
                inputMode="numeric"
                placeholder="Postnummer"
                value={shippingZip}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value.replace(
                    /\D/g,
                    "",
                  );
                  setShippingZip(value);
                }}
                autoComplete="postal-code"
                error={shippingError ? " " : undefined}
              />
              <InputField
                label="Ort"
                name="shippingAddress.city"
                required
                type="text"
                value={shippingCity}
                onChange={(e) =>
                  setShippingCity((e.target as HTMLInputElement).value)
                }
                placeholder="Ange ort"
                autoComplete="address-level2"
                error={shippingError ? " " : undefined}
              />
            </div>
            {shippingError && (
              <p className="col-span-2 text-sm text-red-500">{shippingError}</p>
            )}
          </InputGroup>
        )}

        <Divider />
      </>

      <InputCheckbox name="user.tocAccept" required>
        <p className="ml-4 font-semibold leading-tight">
          Jag godkänner{" "}
          <Link
            href="/anvandarvillkor"
            className="text-blue-link underline"
            target="_blank"
          >
            användarvillkoren
          </Link>{" "}
          och{" "}
          <Link
            href={`/prenumerera/prenumerationsvillkor`}
            className="text-blue-link underline"
            target="_blank"
          >
            prenumerationsvillkor
          </Link>{" "}
          samt bekräftar att jag tagit del av EFNs{" "}
          <Link
            href="/integritetspolicy"
            className="text-blue-link underline"
            target="_blank"
          >
            integritetspolicy
          </Link>
          . EFN ansvarar för kundrelationen samt för behandlingen av dina
          personuppgifter.
        </p>
      </InputCheckbox>

      <Divider />

      <div className="self-end flex flex-col items-end space-y-2">
        <div className="flex justify-end w-full">
          <Button type="submit" showLoader className="btn-large btn-comp">
            Fortsätt
          </Button>
        </div>
        {(addressError || shippingError || submitError) && (
          <p className="text-red-500 font-semibold">
            Var god kontrollera uppgifterna
          </p>
        )}
      </div>
    </form>
  );
};

export default DetailsForm;
